import { sessionStore } from '@/lib/storage';
import { useShutdownStore } from '@/shell/Shutdown/store';
import { useCrashStore } from './store';

/**
 * The odds that any given click or keystroke takes the machine down.
 *
 * XP fell over rarely enough to be a shock and often enough to be a punchline,
 * and the number below is chosen for the same feeling: a visitor doing forty
 * or so things on the desktop has a few percent chance of meeting one, so most
 * people never see it and the ones who do have a story.
 *
 * One constant, deliberately — this is the dial.
 */
export const CRASH_ODDS = 1 / 1000;

/**
 * Actions at the start of a visit that cannot crash, so nobody's *first* click
 * blue-screens the site and reads as a page that is simply broken.
 */
const GRACE_ACTIONS = 12;

/** Set once a session has had its blue screen. One per visit is the joke; two is a bug. */
const CRASHED_KEY = 'teonio.bsod';

/** Force one for a screenshot or a demo: `?bsod=1`. */
function forced(): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('bsod') === '1';
}

/**
 * Everything that has to be true before the dice are worth rolling, kept apart
 * from the roll itself so the odds are the only random thing in here.
 */
function armed(actions: number): boolean {
  if (actions <= GRACE_ACTIONS) return false;
  if (sessionStore.get(CRASHED_KEY) === '1') return false;
  // The machine is already on its way off, or already down.
  if (useShutdownStore.getState().phase !== null) return false;
  if (useCrashStore.getState().bugcheck !== null) return false;
  return true;
}

/**
 * A keystroke that is a *thing the user did*. Holding Shift is not an action,
 * and neither is the hundredth auto-repeat of a held arrow key — counting
 * those would make leaning on a key the fastest way to crash the machine.
 */
function isAction(event: Event): boolean {
  if (!(event instanceof KeyboardEvent)) return true;
  if (event.repeat) return false;
  return !['Shift', 'Control', 'Alt', 'Meta', 'CapsLock'].includes(event.key);
}

/**
 * Wire the dice up to the desktop. Returns the teardown, in the shape every
 * other listener installer here uses.
 */
export function installCrashRoulette(random: () => number = Math.random): () => void {
  if (typeof window === 'undefined') return () => {};

  if (forced()) {
    useCrashStore.getState().crash();
    return () => {};
  }

  let actions = 0;

  const roll = (event: Event) => {
    if (!isAction(event)) return;
    actions += 1;
    if (!armed(actions)) return;
    if (random() >= CRASH_ODDS) return;
    sessionStore.set(CRASHED_KEY, '1');
    useCrashStore.getState().crash();
  };

  // Capture, so a handler that stops the event on its way down the tree cannot
  // also stop the machine from crashing — every click counts, wherever it lands.
  window.addEventListener('pointerdown', roll, { capture: true, passive: true });
  window.addEventListener('keydown', roll, { capture: true, passive: true });

  return () => {
    window.removeEventListener('pointerdown', roll, { capture: true });
    window.removeEventListener('keydown', roll, { capture: true });
  };
}
